// Phase 4c.7 — table tile. V-shape-native: resolves each `(column …)`
// `:ref` to a column, zips the columns into row records, and renders
// with the same mantine `DataTable` the prior panel used. Synthetic
// `c{i}` accessors avoid collisions when two columns project the same
// underlying name.

import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';

import type { ColumnFormat } from '#core/lib/expr/ops/dashboard/enums.ts';
import type { TableSpec } from '#core/lib/expr/ops/dashboard/value.ts';

import { cellScalar } from './cell.ts';
import classes from './TablePanel.module.css';
import { useResolvedColumns } from './useResolvedColumns.ts';

const renderCell = (value: number | string | null, format: ColumnFormat) => {
  if (value === null) {
    return '—';
  }
  if (format === 'datetime') {
    return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD HH:mm') : String(value);
  }
  if (format === 'percent' && typeof value === 'number') {
    return `${value.toLocaleString('en', { maximumFractionDigits: 1 })}%`;
  }
  if (format === 'currency' && typeof value === 'number') {
    return `$${value.toLocaleString('en', { maximumFractionDigits: 2 })}`;
  }
  if (format === 'number' && typeof value === 'number') {
    return value.toLocaleString('en');
  }
  // Struct sub-path refs now resolve to scalar leaves via
  // `projectColumnRef` (Part 1/4), so the `{space,externalId}`
  // direct-relation stringify hack is gone. A ref to a *whole*
  // struct/dataset column still yields a non-scalar leftover — JSON
  // is a sane generic fallback there.
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

export function TablePanel({ spec }: { spec: TableSpec }) {
  const cols = useResolvedColumns(spec.columns.map((c) => c.ref));
  const rowCount = cols.reduce((max, col) => Math.max(max, col.length), 0);

  const records = Array.from({ length: rowCount }, (_, r) => {
    const record: Record<string, number | string | null> = {};
    cols.forEach((col, i) => {
      record[`c${i}`] = cellScalar(col[r]);
    });
    return record;
  });

  return (
    <div className={classes.table}>
      <DataTable<Record<string, number | string | null>>
        columns={spec.columns.map((c, i) => ({
          accessor: `c${i}`,
          title: c.label,
          textAlign: c.align ?? 'left',
          render: (record) => renderCell(record[`c${i}`] ?? null, c.format),
        }))}
        defaultColumnProps={{ noWrap: true }}
        records={records}
        // style={{ maxHeight: '20rem', overflow: 'auto' }}
        withTableBorder={false}
      />
    </div>
  );

  // export const Table = createGridComponent(
  //   ({ columns, datasets, inputDataset }) => {
  //     const dataset = datasets.get(inputDataset);
  //     const records = dataset.rows;
  //     return (
  //       <div className={classes.filterableTable}>
  //         <DataTable<Record<string, unknown>>
  //           columns={columns.map((col) => ({
  //             accessor: col.source ?? '',
  //             title: col.label,
  //             textAlign: col.align,
  //             render: (record) => {
  //               if (col.format === 'datetime') {
  //                 return <div>{dayjs(record[col.source ?? ''] as string).toLocaleString()}</div>;
  //               }
  //               return <div>{record[col.source ?? ''] as any}</div>;
  //             },
  //           }))}
  //           defaultColumnProps={{ noWrap: true }}
  //           records={records}
  //           withTableBorder={false}
  //         />
  //       </div>
  //     );
  //   },
}
